import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AdminUser } from '../types';
import { adminAPI } from '../services/api';

interface AuthContextType {
  user: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<AdminUser>) => void;
  role: string;
  permissions: string[];
  sessionExpiresAt: number | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);

  const isAuthenticated = !!user;
  const role = user?.role || '';
  const permissions = user?.permissions || [];

  // Check for existing user on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Auto logout when session expires
  useEffect(() => {
    if (!sessionExpiresAt) return;

    const checkExpiration = () => {
      const now = Date.now();
      if (now >= sessionExpiresAt) {
        console.log('[Auth] Session expired, logging out...');
        // Clear storage and state directly without calling logout to avoid dependency
        setUser(null);
        setSessionExpiresAt(null);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        localStorage.removeItem('admin_session_expires');
        sessionStorage.removeItem('admin_user');
        sessionStorage.removeItem('admin_session_expires');
      }
    };

    // Check immediately
    checkExpiration();

    // Check every minute
    const interval = setInterval(checkExpiration, 60 * 1000);

    return () => clearInterval(interval);
  }, [sessionExpiresAt]);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('admin_token');
      const storedUser = localStorage.getItem('admin_user') || sessionStorage.getItem('admin_user');
      const storedExpiresAt = localStorage.getItem('admin_session_expires') || sessionStorage.getItem('admin_session_expires');
      
      if (token && storedUser) {
        const userData = JSON.parse(storedUser);
        const expiresAt = storedExpiresAt ? parseInt(storedExpiresAt) : null;
        
        // Check if session has expired
        if (expiresAt && Date.now() >= expiresAt) {
          console.log('[Auth] Session expired, clearing storage...');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          localStorage.removeItem('admin_session_expires');
          sessionStorage.removeItem('admin_user');
          sessionStorage.removeItem('admin_session_expires');
          setUser(null);
          setSessionExpiresAt(null);
          setIsLoading(false);
          return;
        }
        
        setUser(userData);
        setSessionExpiresAt(expiresAt);
        
        // Verify token is still valid
        const response = await adminAPI.getCurrentAdmin();
        if (response.success && response.data) {
          setUser(response.data);
          // Preserve session expiration - if not found, set new 12-hour expiration
          let sessionExpires = expiresAt;
          if (!sessionExpires || Date.now() >= sessionExpires) {
            // Session expired or not found, set new expiration (12 hours from now)
            sessionExpires = Date.now() + (12 * 60 * 60 * 1000);
          }
          setSessionExpiresAt(sessionExpires);
          // Store in the same storage where it was originally stored
          if (localStorage.getItem('admin_user')) {
            localStorage.setItem('admin_user', JSON.stringify(response.data));
            localStorage.setItem('admin_session_expires', sessionExpires.toString());
          } else {
            sessionStorage.setItem('admin_user', JSON.stringify(response.data));
            sessionStorage.setItem('admin_session_expires', sessionExpires.toString());
          }
        } else {
          // Token is invalid, clear storage
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          localStorage.removeItem('admin_session_expires');
          sessionStorage.removeItem('admin_user');
          sessionStorage.removeItem('admin_session_expires');
          setUser(null);
          setSessionExpiresAt(null);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_session_expires');
      sessionStorage.removeItem('admin_user');
      sessionStorage.removeItem('admin_session_expires');
      setUser(null);
      setSessionExpiresAt(null);
    } finally {
      setIsLoading(false);
    }
  };


  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await adminAPI.authenticateAdmin(email, password);
      
      if (response.success && response.data) {
        setUser(response.data);
        
        // Set session expiration to 12 hours from now
        const expiresAt = Date.now() + (12 * 60 * 60 * 1000); // 12 hours in milliseconds
        setSessionExpiresAt(expiresAt);
        
        if (rememberMe) {
          localStorage.setItem('admin_user', JSON.stringify(response.data));
          localStorage.setItem('admin_session_expires', expiresAt.toString());
        } else {
          // Store in sessionStorage for session-only persistence
          sessionStorage.setItem('admin_user', JSON.stringify(response.data));
          sessionStorage.setItem('admin_session_expires', expiresAt.toString());
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await adminAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setSessionExpiresAt(null);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_session_expires');
      sessionStorage.removeItem('admin_user');
      sessionStorage.removeItem('admin_session_expires');
    }
  };

  const updateUser = (userData: Partial<AdminUser>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('admin_user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    role,
    permissions,
    sessionExpiresAt,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
